import { getDefaultAccessors } from 'src/graphql/entities/custom-view/custom-view-config/utils';
import { CustomViewSortConfigItem } from 'src/graphql/entities/custom-view/custom-view.types';

// The attributes listed here are the only identifiers that will be stored in the database
// to track the user's selection. These attributes should remain abstract and not be
// directly tied to the database schema. Instead, they serve as logical names for the attributes,
// which can be matched to their corresponding configuration and settings in the system.
export const SiteAttribute = {
  id: 'id',
  city: 'city',
  siteName: 'siteName',
  siteAddress: 'siteAddress',
  businessName: 'businessName',
  state: 'state',
  zip: 'zip',
  cppExpDate: 'cppExpDate',
  siteChargifySubId: 'siteChargifySubId',
  clientActivatedMembership: 'clientActivatedMembership',
  siteClientSegment: 'siteClientSegment',
  siteTOS: 'siteTOS',
  isMonitored: 'isMonitored',
  ownerName: 'ownerName',
  membershipRegistered: 'membershipRegistered',
  siteLabel: 'siteLabel',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',

  partnerName: 'partnerName',
  timeZone: 'timeZone',
  partnerId: 'partnerId',

  clientName: 'clientName',
  clientPhone: 'clientPhone',
  clientEmail: 'clientEmail',

  projectId: 'projectId',
  projectName: 'projectName',
  projectStage: 'projectStage',
  projectEstDay1: 'projectEstDay1',
  projectDay1StartDate: 'projectDay1StartDate',
  projectWarrantyExpiration: 'projectWarrantyExpiration',
} as const;

export type SiteAttributeType = (typeof SiteAttribute)[keyof typeof SiteAttribute];

export const SiteAttributeAccessors: Record<SiteAttributeType, CustomViewSortConfigItem> = {
  [SiteAttribute.id]: getDefaultAccessors('id'),
  [SiteAttribute.siteName]: {
    sort: ['city', 'address1', 'address2'],
    filter: 'addressId',
    display: 'fullAddress',
  },
  [SiteAttribute.siteAddress]: {
    sort: ['address1', 'address2'],
    filter: SiteAttribute.siteAddress,
    display: 'address',
  },
  [SiteAttribute.city]: getDefaultAccessors('city'),
  [SiteAttribute.state]: getDefaultAccessors('state'),
  [SiteAttribute.businessName]: getDefaultAccessors('businessName'),
  [SiteAttribute.zip]: getDefaultAccessors('zip'),

  [SiteAttribute.cppExpDate]: getDefaultAccessors('cppExpDate'),
  [SiteAttribute.siteChargifySubId]: getDefaultAccessors('chargifySubId'),
  [SiteAttribute.clientActivatedMembership]: {
    sort: 'platformSlaId',
    filter: 'platformSlaId',
    display: 'platformSla.displayName',
  },
  [SiteAttribute.siteTOS]: getDefaultAccessors('tosAccepted'),
  [SiteAttribute.membershipRegistered]: getDefaultAccessors('creditCardProvided'),
  [SiteAttribute.isMonitored]: {
    ...getDefaultAccessors('isMonitored'),
    aggFunction: 'bool_or',
  },
  [SiteAttribute.siteClientSegment]: {
    sort: 'brand/name',
    filter: 'brand/id',
    display: 'brand.name',
  },
  [SiteAttribute.ownerName]: {
    sort: ['platformUser/firstName', 'platformUser/lastName'],
    filter: 'platformUser/id',
    display: 'ownerFullName',
  },
  [SiteAttribute.createdAt]: getDefaultAccessors('createdAt'),
  [SiteAttribute.updatedAt]: getDefaultAccessors('updatedAt'),
  [SiteAttribute.partnerName]: getDefaultAccessors('partner.businessName'),
  [SiteAttribute.timeZone]: getDefaultAccessors('partner.timeZone'),
  [SiteAttribute.partnerId]: getDefaultAccessors('partnerId'),
  [SiteAttribute.clientName]: {
    sort: ['primaryClient/firstName', 'primaryClient/lastName'],
    filter: SiteAttribute.clientName,
    display: 'primaryClient.fullName',
  },
  [SiteAttribute.clientPhone]: getDefaultAccessors('primaryClient.primaryPhoneNumber'),
  [SiteAttribute.clientEmail]: {
    sort: 'primaryClient/primaryEmail/value',
    filter: 'primaryClient/primaryEmail/value',
    display: 'primaryClient.primaryEmail',
  },
  [SiteAttribute.projectId]: {
    sort: 'projects/id',
    filter: 'projects/id',
    display: 'primaryProject.id',
  },
  [SiteAttribute.projectName]: {
    sort: 'projects/name',
    filter: 'projects/name',
    display: 'primaryProject.name',
  },
  [SiteAttribute.projectStage]: {
    sort: 'projects/projectStage/name',
    filter: 'projects/projectStage/id',
    display: 'primaryProject.projectStage.name',
  },
  [SiteAttribute.projectEstDay1]: {
    sort: 'projects/estimatedDay1Date',
    filter: 'projects/estimatedDay1Date',
    display: 'primaryProject.estimatedDay1Date',
  },
  [SiteAttribute.projectDay1StartDate]: {
    sort: 'projects/day1StartDate',
    filter: 'projects/day1StartDate',
    display: 'primaryProject.day1StartDate',
  },
  [SiteAttribute.projectWarrantyExpiration]: {
    sort: 'projects/warrantyExpirationDate',
    filter: 'projects/warrantyExpirationDate',
    display: 'primaryProject.warrantyExpirationDate',
  },
  [SiteAttribute.siteLabel]: {
    sort: 'labelAssignments/label/displayName',
    filter: 'labelAssignments/labelId',
    display: 'labelAssignments',
  },
};
